import React, { useState } from 'react';
import { format } from 'date-fns';
import cx from 'classnames';
import { GetStudentSubmissionsQuery } from '../../../../services/graphql/types/graphql';
import { Loader } from '../../../../components/Loader';
import { AssignmentMediaPreview } from '../../../../components/AssignmentMediaPreview';
import { getAssignmentMediaTypeHint } from '../../../../utils/media';

interface StudentAllAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  data: GetStudentSubmissionsQuery | undefined;
  loading: boolean;
  watermark?: string;
}

export const StudentAllAssignmentsModal: React.FC<StudentAllAssignmentsModalProps> = ({
  isOpen,
  onClose,
  studentName,
  data,
  loading,
  watermark,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  const submissionsWithImages = data?.submissions.filter(
    (submission) =>
      submission.artworks.length > 0 &&
      submission.artworks.some((artwork) => artwork.medias.length > 0)
  );

  return (
    <>
      <div
        className={cx(
          'fixed inset-0 bg-black z-50 transition-opacity duration-300 ease-in-out',
          isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
        )}
        onClick={onClose}
      />

      <div
        className={cx(
          'fixed top-0 right-0 h-full w-[600px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col',
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-800">
            {studentName} - All Assignments
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader source="StudentAllAssignmentsModal" />
            </div>
          ) : submissionsWithImages && submissionsWithImages.length > 0 ? (
            <div className="space-y-4">
              {submissionsWithImages.map((submission) => (
                <div
                  key={submission.submissionId}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="mb-3">
                    <div className="text-sm text-gray-500 mb-1">
                      {submission.assignment.course.name} | {submission.assignment.lesson?.name}
                    </div>
                    <div className="text-base font-medium text-gray-800 mb-2">
                      {submission.assignment.name}
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-gray-600">
                      <div>
                        <span className="font-semibold">提交时间: </span>
                        {format(new Date(Number(submission.createAt)), 'yyyy-MM-dd HH:mm:ss')}
                      </div>
                      <div>
                        <span className="font-semibold">最近修改时间: </span>
                        {format(new Date(Number(submission.updateAt)), 'yyyy-MM-dd HH:mm:ss')}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {submission.artworks.map((artwork) =>
                      artwork.medias.map((media) => {
                        const mediaUrl = media.url.replace(/^http:/, 'https:');
                        return (
                        <div
                          key={media.mediaId}
                          className="w-32 h-32 overflow-hidden rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity relative"
                          onClick={() => setPreviewUrl(mediaUrl)}
                        >
                          <AssignmentMediaPreview
                            url={mediaUrl}
                            thumbnail
                            watermark={watermark}
                            mediaTypeHint={getAssignmentMediaTypeHint(mediaUrl, 'thumbnail')}
                          />
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No assignments with images found
            </div>
          )}
        </div>
      </div>

      {previewUrl && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-80 z-[60]"
            onClick={() => setPreviewUrl(null)}
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-8 pointer-events-none">
            <div className="relative w-[90vw] h-[90vh] pointer-events-auto">
              <button
                onClick={() => setPreviewUrl(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2 z-10"
                aria-label="Close preview"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <AssignmentMediaPreview
                url={previewUrl.replace(/^http:/, 'https:')}
                thumbnail={false}
                watermark={watermark}
                mediaTypeHint={getAssignmentMediaTypeHint(
                  previewUrl.replace(/^http:/, 'https:'),
                  'full',
                )}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};
